import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, LogOut, User as UserIcon } from 'lucide-react';
import { User } from '../../services/authService';

interface HeaderProps {
  user: User;
  onMenuClick: () => void;
  onLogout: () => void;
}

const PAGE_TITLES: Record<string, string> = {
  '/apps': 'Apps',
  '/profile': 'Profile',
  '/services': 'Services',
  '/tickets': 'Support Tickets',
  '/documents': 'Documents',
  '/billing': 'Billing',
  '/users': 'Users',
  '/it-assets': 'IT Assets',
  '/legacy-voice': 'Voice Management',
  '/software-development': 'Software Development',
  '/dashboard': 'Dashboard',
};

const Header: React.FC<HeaderProps> = ({ user, onMenuClick, onLogout }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const location = useLocation();
  const title = PAGE_TITLES[location.pathname] || 'Apps';

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-20">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Left side - Menu button (mobile) and title */}
        <div className="flex items-center">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="ml-4 lg:ml-0 text-2xl font-semibold text-gray-900">
            {title}
          </h1>
        </div>

        {/* Right side - User menu */}
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <UserIcon className="h-4 w-4 text-primary-600" />
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-gray-900">{user.firstname} {user.lastname}</p>
              <p className="text-xs text-gray-500">{user.email}</p>
            </div>
          </button>

          {/* Dropdown menu */}
          {showDropdown && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowDropdown(false)}
              />
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-20">
                <div className="py-1">
                  <button
                    onClick={onLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
